/**
 * Message Send Dialog Component
 * 카카오 대리 발송 메시지 전송
 */

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { Loader2, MessageCircle, Send } from "lucide-react";
import { useState } from "react";

interface MessageSendDialogProps {
  receiverId: string;
  senderId: string;
  productId?: number;
  offerId?: number;
  onSent?: () => void;
  trigger?: React.ReactNode;
}

export function MessageSendDialog({
  receiverId,
  senderId,
  productId,
  offerId,
  onSent,
  trigger,
}: MessageSendDialogProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const { toast } = useToast();

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!message.trim()) {
        throw new Error("메시지를 입력해주세요");
      }

      const body: {
        receiver_id: string;
        message_body: string;
        product_id?: number;
        offer_id?: number;
      } = {
        receiver_id: receiverId,
        message_body: message.trim(),
      };

      if (productId) body.product_id = productId;
      if (offerId) body.offer_id = offerId;

      return apiRequest("POST", "/api/market/messages/send", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/messages", receiverId, senderId],
      });
      setOpen(false);
      setMessage("");
      toast({
        title: "메시지가 전송되었습니다",
        description: "카카오톡으로 알림이 전송되었습니다",
      });
      onSent?.();
    },
    onError: (error: Error) => {
      toast({
        title: "메시지 전송 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <MessageCircle className="w-4 h-4 mr-2" />
            메시지 보내기
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>메시지 전송</DialogTitle>
          <DialogDescription>
            OZK가 안전하게 메시지를 전달합니다
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="message">메시지 내용</Label>
            <Textarea
              id="message"
              placeholder="메시지를 입력해주세요"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/500
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              💡 메시지는 카카오톡으로 전달되며, 상대방의 개인정보는 공개되지
              않습니다
            </p>
          </div>

          <Button
            className="w-full"
            onClick={() => sendMessageMutation.mutate()}
            disabled={sendMessageMutation.isPending || !message.trim()}
          >
            {sendMessageMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                전송 중...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                메시지 전송
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}





